import React, { useState } from 'react';
import { Sparkline, StatusPill, SecondaryBtn, PrimaryBtn } from '../systemKit';
import PriceChangePill from './PriceChangePill';
import ItemDetail from './ItemDetail';
import { describeAlert, formatDetectionAge } from '../../lib/shrinkflation';
import { describeNeedsReview } from '../../lib/parseGuardrails';

// ─── Inline expandable chip ─────────────────────────────────────────────────
// Both the Shrinkflation and Needs Review chips on item rows render through
// this single primitive. Click toggles a row-spanning detail panel below the
// row (rendered separately by the caller; this component only handles the
// chip's appearance + click affordance).
//
// The chevron (▾ collapsed, ▴ expanded) is the cue that the chip is
// interactive without making it look like a button. Tone = amber on both
// chip variants; icon silhouette is what distinguishes them visually.
//
// Per DESIGN_SYSTEM.md §Inline chip expansion.
function ExpandableChip({ icon, label, expanded, onToggle, title, tone = 'amber' }) {
  return (
    <span
      role="button"
      tabIndex={0}
      title={title}
      onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onToggle?.(); } }}
      className="inline-flex items-center"
      style={{ cursor: 'pointer' }}
    >
      <StatusPill tone={tone}>
        <span className="inline-flex items-center gap-1">
          {icon}
          {label}
          <svg
            width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ transition: 'transform 200ms ease', transform: expanded ? 'rotate(180deg)' : 'none' }}
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </StatusPill>
    </span>
  );
}

// Icon SVGs are exported separately so the rendered chevron above can sit
// next to them without re-importing in callers.
const SHRINKFLATION_ICON = (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <path d="M12 9v4"/>
    <path d="M12 17h.01"/>
  </svg>
);
const NEEDS_REVIEW_ICON = (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 16v-4"/>
    <path d="M12 8h.01"/>
  </svg>
);

// ─── Detail panels rendered below the row ───────────────────────────────────

function ShrinkflationDetailPanel({ alert, item, onView, onDismiss }) {
  const vendor = item?.vendor_name ? String(item.vendor_name).replace(/\n/g, ' \u00B7 ') : null;
  return (
    <div
      className="px-4 py-3"
      style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}
    >
      <div className="text-xs font-semibold uppercase mb-1" style={{ letterSpacing: '0.06em', color: 'var(--text-warning)' }}>
        Shrinkflation
      </div>
      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {describeAlert(alert)}
      </div>
      <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
        Detected {formatDetectionAge(alert.first_detected_at)}{vendor ? ` \u00B7 ${vendor}` : ''}
      </div>
      <div className="flex items-center justify-end gap-2 mt-2">
        <SecondaryBtn onClick={onView} className="!px-3 !py-1.5 !text-xs">View item</SecondaryBtn>
        <button
          onClick={onDismiss}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 500 }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function NeedsReviewDetailPanel({ item, onReview, onAcceptFix }) {
  const description = describeNeedsReview(item);
  if (!description) return null;
  const isHigh = description.confidence === 'high';
  return (
    <div
      className="px-4 py-3"
      style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}
    >
      <div className="text-xs font-semibold uppercase mb-1" style={{ letterSpacing: '0.06em', color: 'var(--text-warning)' }}>
        Needs review
      </div>
      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {description.problem}
      </div>
      <div className="text-xs italic mt-0.5" style={{ color: 'var(--text-muted)' }}>
        {description.trace}
      </div>
      <div className="flex items-center justify-end gap-2 mt-2">
        <SecondaryBtn onClick={onReview} className="!px-3 !py-1.5 !text-xs">Review</SecondaryBtn>
        {isHigh && onAcceptFix && (
          <PrimaryBtn onClick={onAcceptFix} className="!px-3 !py-1.5 !text-xs">Accept fix</PrimaryBtn>
        )}
      </div>
    </div>
  );
}

// ─── Main row ───────────────────────────────────────────────────────────────
// Price Tracker's Price-view row. Architectural rule: Price view shows Price metrics only —
// no $ Impact / $ Spend (those are spend metrics; they live in Operations > Food Costs). See
// DESIGN_SYSTEM.md §Three concepts.
//
// Catch-weight items: $/lb is the primary metric; $/case is demoted to a small gray secondary
// line. Price change % is computed against the $/lb series, not $/case, so pack-weight variance
// doesn't register as a phantom price spike.
export default function ItemRow({
  item,
  expanded,
  onToggleExpand,
  priceHistory,
  regional,
  companyRegion,
  foodItems,
  rangeDays,
  expandedHistoryEntries,
  onToggleHistoryEntry,
  editingHistoryEntry,
  editPriceValue, setEditPriceValue,
  editQtyValue, setEditQtyValue,
  editLineTotalValue, setEditLineTotalValue,
  onStartEditEntry,
  onCancelEditEntry,
  onSaveEditEntry,
  activeAlert,
  needsReviewItem,
  onAlertView,
  onAlertDismiss,
  onNeedsReviewReview,
  onNeedsReviewAccept,
}) {
  const isOverpaying = regional && regional.isActive && regional.diff > 2;
  const isCW = !!item.is_catch_weight;

  // Per-row chip expand state. Local — naturally resets on filter / sort
  // because the row unmounts. Two chips can be open at once on the same row.
  const [chipsOpen, setChipsOpen] = useState({ shrinkflation: false, needsReview: false });

  // Catch-weight items: compute change % against price_per_lb series. For fixed-pack,
  // the existing food_items.price_change_pct is already case-based and correct.
  let displayChangePct = item.price_change_pct;
  if (isCW && Array.isArray(priceHistory) && priceHistory.length >= 2) {
    const perLbSeries = priceHistory.filter(h => h.price_per_lb != null).map(h => Number(h.price_per_lb));
    if (perLbSeries.length >= 2) {
      const latest = perLbSeries[perLbSeries.length - 1];
      const prev = perLbSeries[perLbSeries.length - 2];
      displayChangePct = prev > 0 ? Math.round(((latest - prev) / prev) * 10000) / 100 : null;
    }
  }

  const sparkData = Array.isArray(priceHistory)
    ? priceHistory.map(h => (isCW && h.price_per_lb != null) ? Number(h.price_per_lb) : Number(h.price)).filter(v => isFinite(v))
    : [];

  // Tooltips for hover hint (the panel below is the canonical source of detail).
  const shrinkfTooltip = activeAlert ? `${describeAlert(activeAlert)}. Click to expand.` : '';
  const needsReviewItemRecord = needsReviewItem || null;

  return (
    <>
      <tr
        className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${expanded ? 'bg-gray-50' : ''}`}
        data-food-item-id={item.id}
        onClick={onToggleExpand}
      >
        <td className="px-4 py-3 font-medium text-gray-900">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isOverpaying
              ? <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0 animate-pulse" title={`Paying ${regional.diff.toFixed(1)}% above regional avg`} />
              : <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd"/></svg>}
            <span>{item.normalized_name}</span>
            {needsReviewItemRecord && (
              <ExpandableChip
                icon={NEEDS_REVIEW_ICON}
                label="Needs review"
                expanded={chipsOpen.needsReview}
                onToggle={() => setChipsOpen(s => ({ ...s, needsReview: !s.needsReview }))}
                title="This item's pack/weight data looks suspicious. Click to expand."
              />
            )}
            {activeAlert && (
              <ExpandableChip
                icon={SHRINKFLATION_ICON}
                label="Shrinkflation"
                expanded={chipsOpen.shrinkflation}
                onToggle={() => setChipsOpen(s => ({ ...s, shrinkflation: !s.shrinkflation }))}
                title={shrinkfTooltip}
              />
            )}
            {isCW && <StatusPill tone="neutral">catch-weight</StatusPill>}
            {item.product_code && <span className="text-gray-300 font-normal ml-1" style={{ fontSize: 9 }}>#{item.product_code}</span>}
          </div>
        </td>
        <td className="px-4 py-3">
          {item.category && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{item.category}</span>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          {isCW ? (
            <>
              <div className="font-semibold text-gray-900">
                {item.price_per_lb != null ? `$${Number(item.price_per_lb).toFixed(2)}/lb` : '--'}
              </div>
              <div className="text-gray-400" style={{ fontSize: 11 }}>
                {item.current_price != null ? `$${Number(item.current_price).toFixed(2)}/case` : ''}
              </div>
            </>
          ) : (
            <>
              <div className="font-semibold text-gray-900">
                {item.current_price != null ? `$${Number(item.current_price).toFixed(2)}` : '--'}
              </div>
              {item.price_per_lb != null && (
                <div className="text-gray-400" style={{ fontSize: 11 }}>
                  ${Number(item.price_per_lb).toFixed(2)}/lb
                </div>
              )}
            </>
          )}
        </td>
        <td className="px-4 py-3 text-right text-gray-500">
          {item.previous_price != null ? `$${Number(item.previous_price).toFixed(2)}` : '--'}
        </td>
        <td className="px-4 py-3 text-center">
          <PriceChangePill pct={displayChangePct} />
        </td>
        <td className="px-4 py-3">
          <Sparkline data={sparkData} tone="price" />
        </td>
      </tr>

      {/* Chip detail panels — full-width row(s) below the main row. Stack
          when both chips are open. Click here doesn't bubble to the row toggle. */}
      {chipsOpen.needsReview && needsReviewItemRecord && (
        <tr onClick={(e) => e.stopPropagation()}>
          <td colSpan={6} className="px-0 py-0">
            <NeedsReviewDetailPanel
              item={needsReviewItemRecord}
              onReview={() => onNeedsReviewReview?.(needsReviewItemRecord)}
              onAcceptFix={onNeedsReviewAccept ? () => onNeedsReviewAccept(needsReviewItemRecord) : null}
            />
          </td>
        </tr>
      )}
      {chipsOpen.shrinkflation && activeAlert && (
        <tr onClick={(e) => e.stopPropagation()}>
          <td colSpan={6} className="px-0 py-0">
            <ShrinkflationDetailPanel
              alert={activeAlert}
              item={item}
              onView={() => onAlertView?.(activeAlert)}
              onDismiss={() => onAlertDismiss?.(activeAlert)}
            />
          </td>
        </tr>
      )}

      {expanded && (
        <tr>
          <td colSpan={6} className="px-0 py-0">
            <div className="bg-gray-50 border-t border-b border-gray-100 px-6 py-4">
              <ItemDetail
                item={item}
                priceHistory={priceHistory}
                regional={regional}
                companyRegion={companyRegion}
                foodItems={foodItems}
                rangeDays={rangeDays}
                expandedHistoryEntries={expandedHistoryEntries}
                onToggleHistoryEntry={onToggleHistoryEntry}
                editingHistoryEntry={editingHistoryEntry}
                editPriceValue={editPriceValue}
                setEditPriceValue={setEditPriceValue}
                editQtyValue={editQtyValue}
                setEditQtyValue={setEditQtyValue}
                editLineTotalValue={editLineTotalValue}
                setEditLineTotalValue={setEditLineTotalValue}
                onStartEditEntry={onStartEditEntry}
                onCancelEditEntry={onCancelEditEntry}
                onSaveEditEntry={onSaveEditEntry}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
